import { datadogLogs } from "@datadog/browser-logs";
import type { PaymentIntent, Stripe, StripeElements } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { computed, ref } from "vue";
import type StripeDepositFormType from "@/components/forms/PayDeposit/StripeDepositForm.vue";
import { STRIPE_ERRORS } from "@/constants";
import { hasTextValue } from "@/util/string";
import { paymentIntentId } from "@/util/stripe";
import type { DepositModelData } from "./types/deposit";
import type { StripeChargePayload, StripeIntentPayload } from "./types/stripe";
import { useDepositModels } from "./useDepositModels";

export const useStripe = () => {
  const applicationId = ref<string | undefined>();
  const stripeComponentRef = ref<typeof StripeDepositFormType | undefined>();

  const cardSectionLoading = ref<boolean>(false);
  const stripeClient = ref<Stripe | null>(null);
  const stripeElements = ref<StripeElements>();
  const stripeRendered = ref<boolean>(false);
  const clientSecret = ref<string | undefined>();
  // Stripe.js is the only consumer allowed the full client secret. Everything we store,
  // submit or report uses the payment intent id instead.
  const paymentReference = computed<string | undefined>(() => paymentIntentId(clientSecret.value));
  const paymentStatus = ref<string | undefined>();

  const paymentSuccess = ref<boolean>(false);
  const stripePaymentError = ref<boolean>(false);
  const stripePaymentErrorType = ref<string>("");
  const paymentAlreadyExist = ref<boolean>(false);
  const stripeStatusMessage = ref<string | undefined>();

  const { getStripeChargePayload, getStripeIntentPayload } = useDepositModels();

  const setApplicationId = (id: string) => {
    applicationId.value = id;
  };

  const setStripeComponentRef = (componentRef: typeof StripeDepositFormType) => {
    stripeComponentRef.value = componentRef;
  };

  const getStripeClient = async (): Promise<Stripe | null> => {
    if (!stripeClient.value) {
      stripeClient.value = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_API.toString());
    }
    return stripeClient.value;
  };

  const getExistingIntents = async (): Promise<PaymentIntent[]> => {
    const { data } = await axios.get(`/api/stripe-intents/${applicationId.value}`);
    return data?.data;
  };

  const createPaymentIntent = async (payload: StripeIntentPayload): Promise<void> => {
    const { data } = await axios.post("/api/payment-intent", payload);
    clientSecret.value = data?.clientSecret as string | undefined;
  };

  const initializePaymentIntent = async (payload: StripeIntentPayload) => {
    try {
      if (paymentAlreadyExist.value) return;

      const existingIntents = await getExistingIntents();
      const succeededIntents = existingIntents.filter((intent) => intent.status === "succeeded");
      const pendingIntents = existingIntents.filter(
        (intent) => intent.status !== "succeeded" && intent.status !== "canceled",
      );
      // Effectively ranking intents
      const validIntents = [...succeededIntents, ...pendingIntents];
      if (validIntents.length > 0) {
        // Take the first intent
        clientSecret.value = validIntents[0].client_secret ?? undefined;
      }

      if (!clientSecret.value) {
        await createPaymentIntent(payload);
      }

      const appearance = {
        variables: {
          fontFamily: "Montserrat",
          fontSizeSm: "14px",
          fontSmooth: "auto",
          fontWeightNormal: "500",
          colorPrimary: "rgb(30, 38, 76)",
          colorText: "rgb(30, 38, 76)",
          borderRadius: "0px",
        },
      };
      const options = {
        fonts: [
          {
            cssSrc: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;700&display=swap",
          },
        ],
      };
      stripeElements.value = (await getStripeClient())?.elements({
        clientSecret: clientSecret.value,
        appearance,
        ...options,
      });
      if (stripeComponentRef.value) {
        stripeComponentRef.value.mountPayment(stripeElements.value);
        stripeRendered.value = true;
      }
    } catch (error) {
      stripeRendered.value = false;
      if (error instanceof Error) {
        datadogLogs.logger.error(
          "Issue while mounting Stripe in HTML.",
          { application_id: applicationId.value, stripe_payment_reference: paymentReference.value },
          error,
        );
      }
      console.error(error);
    }
  };

  const checkDepositDataForStripe = async (data: DepositModelData, isCubaProgram: boolean): Promise<void> => {
    if (
      hasTextValue(data.billingAddress.street_address_1) &&
      hasTextValue(data.billingAddress.city) &&
      hasTextValue(data.billingAddress.state) &&
      hasTextValue(data.billingAddress.postal_code) &&
      hasTextValue(data.person.first_name) &&
      hasTextValue(data.person.last_name) &&
      hasTextValue(data.permanentAddress.street_address_1) &&
      hasTextValue(data.permanentAddress.city) &&
      hasTextValue(data.permanentAddress.state) &&
      hasTextValue(data.permanentAddress.postal_code) &&
      !isCubaProgram &&
      !stripeRendered.value
    ) {
      cardSectionLoading.value = true;
      const stripeChargePayload = getStripeChargePayload(data);
      await checkIfPaymentExists(stripeChargePayload);
      const stripeIntentPayload = getStripeIntentPayload(data);
      await initializePaymentIntent(stripeIntentPayload);
      cardSectionLoading.value = false;
    }
  };

  const updateStatus = async (newStatus: string) => {
    try {
      await axios.put(`/api/stripe-charge/${applicationId.value}`, { newStatus: newStatus });
    } catch (error) {
      if (error instanceof Error) {
        datadogLogs.logger.error(
          "Error while setting up new payment status",
          { application_id: applicationId.value },
          error,
        );
      } else {
        console.error(error);
      }
    }
  };

  const savePaymentCharge = async (payload: StripeChargePayload) => {
    try {
      const chargePayload: StripeChargePayload = { ...payload, status: paymentStatus.value };
      await axios.post("/api/stripe-charge", chargePayload);
    } catch (error) {
      if (error instanceof Error) {
        datadogLogs.logger.error(
          "Error while creating payment event in Connect",
          { application_id: applicationId.value },
          error,
        );
      }
    }
  };

  const checkStripePaymentStatus = async (payload: StripeChargePayload) => {
    if (!clientSecret.value || clientSecret.value === "null") {
      return;
    }
    try {
      const client = await getStripeClient();
      if (!client) {
        throw new Error("Unable to obtain stripe client");
      }
      const { paymentIntent } = await client.retrievePaymentIntent(clientSecret.value);

      if (!paymentIntent) {
        throw new Error("Unable to retrieve payment intent");
      }

      if (paymentStatus.value && paymentStatus.value !== paymentIntent.status && paymentAlreadyExist.value) {
        await updateStatus(paymentIntent.status);
      }

      paymentStatus.value = paymentIntent.status;
      switch (paymentIntent.status) {
        case "succeeded":
          stripePaymentError.value = false;
          stripePaymentErrorType.value = "";
          stripeStatusMessage.value = "Payment succeeded. Please check your email for your receipt.";
          paymentSuccess.value = true;
          if (!paymentAlreadyExist.value) await savePaymentCharge(payload);
          break;
        case "processing":
          stripeStatusMessage.value = "Your payment is processing.";
          if (!paymentAlreadyExist.value) await savePaymentCharge(payload);
          break;
        case "requires_action":
          stripeStatusMessage.value = "Your payment requires additional actions.";
          break;
        default:
          break;
      }
    } catch (error) {
      if (error instanceof Error) {
        datadogLogs.logger.error(
          "Error while trying to check payment intent status.",
          {
            application_id: applicationId.value,
            stripe_payment_reference: paymentReference.value,
            stripeStatus: stripeStatusMessage.value,
            paymentExists: paymentAlreadyExist.value,
          },
          error,
        );
      }
    }
  };

  interface PaymentExistStatus {
    exists: boolean;
    status?: string;
  }

  const paymentExists = async (applicationId: string): Promise<PaymentExistStatus> => {
    try {
      const response = await axios.get(`/api/stripe-charge/${applicationId}`);
      return {
        exists: true,
        status: response.data?.status,
      };
    } catch (error) {
      return { exists: false };
    }
  };

  const checkIfPaymentExists = async (payload: StripeChargePayload) => {
    const hasPayment = await paymentExists(applicationId.value ?? "");

    if (hasPayment?.exists) {
      paymentAlreadyExist.value = true;
      paymentStatus.value = hasPayment?.status;
      paymentSuccess.value = hasPayment?.status ? hasPayment.status === "succeeded" : false;

      await checkStripePaymentStatus(payload);
    }
  };

  const handleSubmitToStripe = async (payload: StripeChargePayload, paymentType: string) => {
    await checkIfPaymentExists(payload);
    if (paymentAlreadyExist.value) return;

    const client = await getStripeClient();
    const elements = stripeElements.value;
    if (!(client && elements)) return;
    const { error } = await client.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
        receipt_email: (document.getElementById("email") as HTMLInputElement)?.value,
      },
      redirect: "if_required",
    });

    if (error?.type === "card_error" || error?.type === "validation_error") {
      // https://stripe.com/docs/declines/codes
      try {
        stripePaymentError.value = true;
        stripePaymentErrorType.value = error.decline_code
          ? STRIPE_ERRORS[error.decline_code]
          : STRIPE_ERRORS[error.code ?? "default"];
        datadogLogs.logger.error("Card Payment Issue", {
          application_id: applicationId.value,
          stripe_payment_reference: paymentReference.value,
          error: error?.message,
        });
      } catch (error) {
        if (error instanceof Error) {
          datadogLogs.logger.error(
            "Issue while trying to assign error code",
            { application_id: applicationId.value },
            error,
          );
        }
        stripePaymentError.value = true;
        stripePaymentErrorType.value = STRIPE_ERRORS.default;
      }
    } else if (
      error?.type === "api_error" ||
      error?.type === "idempotency_error" ||
      error?.type === "invalid_request_error"
    ) {
      stripePaymentError.value = true;
      stripePaymentErrorType.value = STRIPE_ERRORS.api_error;
      datadogLogs.logger.error("Stripe request issue", {
        error: error?.message,
        application_id: applicationId.value,
        stripe_payment_reference: paymentReference.value,
      });
    } else if (error) {
      stripePaymentError.value = true;
      stripePaymentErrorType.value = STRIPE_ERRORS.default;
      datadogLogs.logger.error("Stripe error", {
        error: error?.message,
        application_id: applicationId.value,
        stripe_payment_reference: paymentReference.value,
      });
    }
    await checkStripePaymentStatus(payload);
  };

  return {
    transactionId: paymentReference,
    stripePaymentError,
    stripePaymentErrorType,
    paymentAlreadyExist,
    stripeStatusMessage,
    paymentSuccess,
    paymentStatus,
    cardSectionLoading,
    setApplicationId,
    setStripeComponentRef,
    handleSubmitToStripe,
    checkDepositDataForStripe,
    savePaymentCharge,
  };
};
